/* ==========================================================================
   CLIMAI / PROYECTOS — climai-pro.js
   Fase 1 · Vanilla, sin dependencias, sin build.
   Principios:
     · El contenido nunca depende del JS para ser legible ni interactivo.
     · Ninguna animacion bloquea la interaccion.
     · prefers-reduced-motion se respeta en todo.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('js');

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Analitica — emisor unico. No instala nada. Empuja a dataLayer si existe y
     dispara un CustomEvent para que cualquier pixel presente pueda escucharlo. */
  function track(name, detail) {
    var payload = detail || {};
    try {
      window.dataLayer = window.dataLayer || [];
      var obj = { event: name };
      for (var k in payload) { if (payload.hasOwnProperty(k)) obj[k] = payload[k]; }
      window.dataLayer.push(obj);
    } catch (e) {}
    try {
      document.dispatchEvent(new CustomEvent('climai:' + name, { detail: payload }));
    } catch (e) {}
  }
  window.climaiTrack = track;

  /* Eventos declarativos: data-pro-event="hero_project_click" */
  document.addEventListener('click', function (e) {
    var el = e.target && e.target.closest ? e.target.closest('[data-pro-event]') : null;
    if (!el) return;
    track(el.getAttribute('data-pro-event'), {
      label: (el.getAttribute('data-pro-event-label') || el.textContent || '').trim().slice(0, 80)
    });
  });

  /* Reveals. El contenido ya esta en el flujo y es interactivo; solo se anima
     opacidad y desplazamiento. */
  var revealedCount = 0;

  function revealAll(scope) {
    var all = (scope || document).querySelectorAll('.pro-reveal:not(.is-visible)');
    Array.prototype.forEach.call(all, function (el) { el.classList.add('is-visible'); });
  }

  function initReveals(scope) {
    var items = (scope || document).querySelectorAll('.pro-reveal:not(.is-visible)');
    if (!items.length) return;

    /* Sin IntersectionObserver, o con un viewport de tamano cero (algunos
       navegadores automatizados y webviews embebidos), no hay interseccion
       posible: mostramos todo de inmediato. */
    if (!('IntersectionObserver' in window) || !window.innerWidth || !window.innerHeight) {
      revealAll(scope);
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        revealedCount++;
        var group = el.closest('[data-pro-stagger]');
        if (group && !reduceMotion.matches) {
          var siblings = Array.prototype.slice.call(group.querySelectorAll('.pro-reveal'));
          var i = siblings.indexOf(el);
          el.style.setProperty('--pro-delay', (i > 0 ? Math.min(i, 8) * 60 : 0) + 'ms');
        }
        el.classList.add('is-visible');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.01 });

    Array.prototype.forEach.call(items, function (el) { io.observe(el); });
  }

  /* Acordeon (FAQ) */
  function initAccordions(scope) {
    var triggers = (scope || document).querySelectorAll('.pro-accordion__trigger');
    Array.prototype.forEach.call(triggers, function (btn) {
      if (btn.dataset.proBound) return;
      btn.dataset.proBound = '1';

      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        var panel = document.getElementById(btn.getAttribute('aria-controls'));
        btn.setAttribute('aria-expanded', String(!expanded));
        if (panel) panel.hidden = expanded;
        if (!expanded) track('faq_open', { question: (btn.textContent || '').trim().slice(0, 120) });
      });
    });
  }

  /* Tabs (perfiles profesionales) — patron WAI-ARIA con flechas */
  function initTabs(scope) {
    var lists = (scope || document).querySelectorAll('[role="tablist"]');
    Array.prototype.forEach.call(lists, function (list) {
      if (list.dataset.proBound) return;
      list.dataset.proBound = '1';

      var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function select(tab, focus) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', String(on));
          t.setAttribute('tabindex', on ? '0' : '-1');
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();
        track('profile_tab', { profile: (tab.textContent || '').trim().slice(0, 60) });
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () { select(tab, false); });
        tab.addEventListener('keydown', function (e) {
          var i = tabs.indexOf(tab), next = null;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (e.key === 'Home') next = tabs[0];
          else if (e.key === 'End') next = tabs[tabs.length - 1];
          if (next) { e.preventDefault(); select(next, true); }
        });
      });
    });
  }

  /* Video diferido. Nunca autoplay sin control visible. Con reduced-motion no
     arranca solo: se queda el poster y el usuario decide. */
  function initVideos(scope) {
    var wraps = (scope || document).querySelectorAll('[data-pro-video]');
    Array.prototype.forEach.call(wraps, function (wrap) {
      if (wrap.dataset.proBound) return;
      wrap.dataset.proBound = '1';

      var video = wrap.querySelector('video');
      var toggle = wrap.querySelector('[data-pro-video-toggle]');
      if (!video) return;

      function setLabel() {
        if (!toggle) return;
        var playing = !video.paused && !video.ended;
        toggle.setAttribute('aria-label', playing ? 'Pausar video' : 'Reproducir video');
        wrap.classList.toggle('is-playing', playing);
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          if (video.paused) {
            var p = video.play();
            if (p && p.then) { p.then(function () { track('video_play', { src: video.currentSrc || '' }); }).catch(function () {}); }
          } else {
            video.pause();
          }
          setLabel();
        });
      }
      video.addEventListener('play', setLabel);
      video.addEventListener('pause', setLabel);

      if (wrap.hasAttribute('data-pro-video-auto') && !reduceMotion.matches
          && 'IntersectionObserver' in window) {
        var vio = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var p = video.play(); if (p && p.catch) p.catch(function () {});
            } else if (!video.paused) {
              video.pause();
            }
            setLabel();
          });
        }, { threshold: 0.35 });
        vio.observe(wrap);
      }

      setLabel();
    });
  }

  function initAll(scope) {
    initReveals(scope);
    initAccordions(scope);
    initTabs(scope);
    initVideos(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(document); });
  } else {
    initAll(document);
  }

  /* Red de seguridad. Si pasado un segundo y medio el observador no ha
     revelado NADA, algo impide que funcione: mostramos todo. El contenido
     nunca puede quedarse invisible de forma permanente. */
  setTimeout(function () {
    if (revealedCount === 0) revealAll(document);
  }, 1500);

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { initAll(e.target); });

  if (reduceMotion.addEventListener) {
    reduceMotion.addEventListener('change', function () { revealAll(document); });
  }
})();
